import { createRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { createAuthToken } from '@/api/internal-token-client'
import { fetchWebDropinAdditionalFields } from '@/api/transactions-client'
import { AdyenDropin, ApplePayButton } from '@/components/ui/adyen'
import { Button } from '@/components/ui/button'
import { checkoutFieldModeToCardConfig, resolveCheckoutFieldMode } from '@/lib/checkout-field-mode'
import { SessionType } from '@/lib/enums'
import { env } from '@/lib/env'
import { postToHost, SUBMIT_LABEL_PAYMENT } from '@/lib/post-message'

import { Route as rootRoute } from './__root'

import type { AdyenDropinHandle } from '@/components/ui/adyen'
import type { CheckoutFieldMode } from '@/lib/checkout-field-mode'
import type { ErrorWithState } from '@/lib/types'

const searchSchema = z.object({
  transactionId: z.string().optional(),
  sessionData: z.string().optional(),
})

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transaction-session',
  validateSearch: (search) => searchSchema.parse(search),
  component: TransactionSessionPage,
})

function TransactionSessionPage() {
  const { transactionId, sessionData } = Route.useSearch()
  if (!sessionData) return <IdleState />
  return <TransactionSessionForm transactionId={transactionId} sessionData={sessionData} />
}

function IdleState() {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <p className="text-foreground text-sm">Invalid URL parameters.</p>
    </div>
  )
}

interface FormProps {
  transactionId?: string
  sessionData: string
}

function TransactionSessionForm({ transactionId, sessionData }: FormProps) {
  const [token, setToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<Error | null>(null)
  const [checkoutFieldMode, setCheckoutFieldMode] = useState<CheckoutFieldMode | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [applePayAvailable, setApplePayAvailable] = useState(false)
  const dropinRef = useRef<AdyenDropinHandle>(null)

  // Token exchange — single shot on mount.
  useEffect(() => {
    let cancelled = false
    createAuthToken(env.INTERNAL_API_HOST, sessionData)
      .then((response) => {
        if (!cancelled) setToken(response.token)
      })
      .catch((error: Error) => {
        if (!cancelled) setTokenError(error)
      })
    return () => {
      cancelled = true
    }
  }, [sessionData])

  // Checkout field mode — company-settings-driven (JET-2000), fetched in
  // parallel with the token exchange above so <AdyenDropin> only ever mounts
  // once the mode is known (mount-time lock-in: a late-resolving mode has no
  // effect once Adyen has already mounted). `fetchWebDropinAdditionalFields`
  // already fails closed to `null` on any error; `resolveCheckoutFieldMode`
  // maps that to `'default'`.
  useEffect(() => {
    let cancelled = false
    async function loadCheckoutFieldMode() {
      const fields = transactionId ? await fetchWebDropinAdditionalFields(env.INTERNAL_API_HOST, transactionId) : null
      if (!cancelled) setCheckoutFieldMode(resolveCheckoutFieldMode(fields))
    }
    loadCheckoutFieldMode()
    return () => {
      cancelled = true
    }
  }, [transactionId])

  const handleReady = () => {
    // Step 8 in the dispatch contract: postMessage card-payment-setup
    // after Adyen mount resolves.
    postToHost('card-payment-setup')
  }

  const handleSubmit = async () => {
    if (!dropinRef.current) return
    setSubmitting(true)
    try {
      const result = await dropinRef.current.submit()
      // Step 10a/b — outcome from submit's resolve path. `resultCode` rides
      // along when Adyen produced the outcome; it is absent for the hook's
      // internal fail-safe sentinels, which `refusal` also carries.
      if (result.refusal) {
        postToHost('card-payment-failed', result.resultCode ? { resultCode: result.resultCode } : undefined)
      } else {
        postToHost('card-payment-completed')
        // AdyenDropin hides itself (form + Button children) on status COMPLETED.
      }
    } catch (error) {
      // Step 10c/d — outcome from submit's throw path.
      const e = error as ErrorWithState
      if (e?.state?.isValid) {
        postToHost('card-payment-error')
      } else {
        postToHost('card-payment-form-invalid')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleApplePaySubmit = async () => {
    if (!dropinRef.current) return
    setSubmitting(true)
    try {
      const result = await dropinRef.current.submitApplePay()
      if (result.refusal) {
        postToHost('card-payment-failed', result.resultCode ? { resultCode: result.resultCode } : undefined)
      } else {
        postToHost('card-payment-completed')
      }
    } catch (error) {
      const e = error as ErrorWithState
      if (e?.name === 'CANCEL') {
        // Sheet dismissed by the user — not a failure.
        postToHost('card-payment-cancelled')
      } else if (e?.state?.isValid) {
        postToHost('card-payment-error')
      } else {
        postToHost('card-payment-form-invalid')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (tokenError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-destructive text-sm">Failed to initialize session.</p>
      </div>
    )
  }

  if (!token || checkoutFieldMode === null) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    )
  }

  const { billingAddressMode, requiresHolderName } = checkoutFieldModeToCardConfig(checkoutFieldMode)

  return (
    <div className="flex min-h-screen flex-col items-center justify-start gap-4 p-6">
      <AdyenDropin
        ref={dropinRef}
        internalApiHost={env.INTERNAL_API_HOST}
        authToken={token}
        adyenClientKey={env.ADYEN_CLIENT_KEY}
        adyenEnvironment={env.ENV === 'production' ? 'live' : 'test'}
        sessionType={SessionType.PAYMENT}
        encodedSessionData={sessionData}
        transactionId={transactionId}
        showCardholderName={requiresHolderName || Boolean(env.WEB_DROP_IN_SHOW_NAME)}
        billingAddressMode={billingAddressMode}
        onReady={handleReady}
        onApplePayAvailableChange={setApplePayAvailable}
        applePaySlot={applePayAvailable && <ApplePayButton onClick={handleApplePaySubmit} disabled={submitting} />}
        className="w-full max-w-md"
      >
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="adyen-checkout__button adyen-checkout__button--pay"
        >
          {SUBMIT_LABEL_PAYMENT}
        </Button>
      </AdyenDropin>
    </div>
  )
}
